/**
 *
 * @module contentClient
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/content.md
 *
 */

import Check from 'check-types';
import Logging from './../../logging/entry';
import Exception from './../exception/entry';
import HttpClient from './../platforms/httpClient';
import Configuration from './../configuration/entry';
import Token from './../token/entry';
import Util from './../util/entry';
import DustLogUtility from './../internal/dust/dustLogUtility';
import DustUrnReference from './../internal/dust/dustUrnReference';

import ContentClientConfiguration from './contentClientConfiguration';
import GraphQlBuilder from './graphQlBuilder';
import ContentClientEndpoint from './contentClientEndpoint';

import SearchOverrides from './../../content/searchOverrides';

const InvalidArgumentException = Exception.InvalidArgumentException;
const ServiceException = Exception.ServiceException;

const AccessToken = Token.AccessToken;
const HttpMethod = Configuration.HttpMethod;
const ContentClientDustUrnReference = DustUrnReference.services.content.contentClient;

const {
    nonEmptyObject,
    integer,
    positive,
    nonEmptyString,
    instanceStrict
} = Check.assert;

/**
 *
 * @class ContentClient
 * @implements {IContentClient}
 * @desc Provides a data client that can be used to access content services.
 *
 */
export default class ContentClient {

    /**
     *
     * @param {ContentClientConfiguration} contentClientConfiguration
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @param {IHttpClient} [httpClient=null]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(contentClientConfiguration, logger = Logging, httpClient = null) {

        const errorMessage = {
            ctor: 'ContentClient(contentClientConfiguration, logger, httpClient)',
            get contentClientConfiguration() {
                return `${this.ctor} needs ContentClientConfiguration instance`;
            }
        };

        instanceStrict(
            contentClientConfiguration,
            ContentClientConfiguration,
            errorMessage.contentClientConfiguration,
            InvalidArgumentException
        );

        /**
         *
         * @access private
         * @type {ContentClientConfiguration}
         * @desc The configuration information to use.
         *
         */
        this.config = contentClientConfiguration;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {HttpClient}
         * @desc The object responsible for making HTTP requests.
         *
         */
        this.httpClient = httpClient || new HttpClient(logger);

        /**
         *
         * @access private
         * @type {Boolean}
         * @note needs to default to true to collect dust events before the configuration is fetched and we can
         * determine if this should be enabled
         *
         */
        this.dustEnabled = true;

        /**
         *
         * @type {String}
         * @desc optional parameter that defines the country of origin for the content request
         *
         */
        this.xGeoOverride = 'X-GEO-OVERRIDE';

        /**
         *
         * @type {String}
         * @desc an optional parameter that can be used to limit search results to a specific time period
         * @note this is not supported in production
         *
         */
        this.xDelorean = 'X-DELOREAN';

        /**
         *
         * @type {String}
         * @desc an optional parameter that is supplied to content requests to identify related content requests
         *
         */
        this.xContentTransactionId = 'X-Content-Transaction-ID';

        this.logger.log(ContentClient.name, 'Created.');

    }

    /**
     *
     * @access public
     * @param {Object} options
     * @param {String} options.context - The GraphQL endpoint context.
     * @param {GraphQlBuilder} options.graphQlBuilder - A GraphQlBuilder object that creates
     * a valid GraphQL query to be used to get the content.
     * @param {SDK.Services.Token.AccessToken} options.accessToken - The current access token.
     * @param {SDK.Content.SearchOverrides} [options.overrides] - Values that can be used to override location and time
     * information that is otherwise automatically derived for search requests.
     * @param {String} [contentTransactionId] - An application developer-provided value to be included with the HTTP
     * request as the value of the `X-Content-Transaction-ID` header.
     * @param {SDK.Content.SearchOverrides} [options.overrides] - Values that can be used to override
     * @desc Returns the results for the query.
     * @returns {Promise<Object>} The results for the query.
     *
     */
    query(options) {

        const errorMessage = {
            method: 'ContentClient.query(options)',
            get options() {
                return `${this.method} options should be a non-empty Object`;
            },
            get context() {
                return `${this.method} options.context should be a String with length`;
            },
            get graphQlBuilder() {
                return `${this.method} needs GraphQlBuilder instance`;
            },
            get accessToken() {
                return `${this.method} needs AccessToken instance`;
            },
            get overrides() {
                return `${this.method} options.overrides should be an instance of SDK.Content.SearchOverrides`;
            },
            get contentTransactionId() {
                return `${this.method} options.contentTransactionId should be a String with length`;
            }
        };

        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);

        const { context, graphQlBuilder, accessToken, overrides, contentTransactionId } = options;

        nonEmptyString(context, errorMessage.context, InvalidArgumentException);
        instanceStrict(graphQlBuilder, GraphQlBuilder, errorMessage.graphQlBuilder, InvalidArgumentException);
        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        if (Check.assigned(overrides)) {
            instanceStrict(overrides, SearchOverrides, errorMessage.overrides, InvalidArgumentException);
        }

        if (Check.assigned(contentTransactionId)) {
            nonEmptyString(contentTransactionId, errorMessage.contentTransactionId, InvalidArgumentException);
        }

        const { dustEnabled, httpClient, logger } = this;

        if (!ContentClientEndpoint[context]) {
            logger.warn(ContentClient.name, `ContentClientEndpoint[${context}] is not defined.`);
        }

        const payload = this.getPayload({
            accessToken,
            rel: context,
            graphQlBuilder,
            overrides,
            contentTransactionId
        });

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: ContentClient.name,
            urn: ContentClientDustUrnReference.query,
            payload,
            method: payload.method
        });

        return httpClient[payload.method](payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility, 'query');

        }).then((response) => {

            const { data } = response;

            return Promise.resolve(data);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @param {String} suggestionSet - The name of the data dictionary used for the suggestions.
     * You must know what valid values are ahead of time.
     * @param {String} query - The case-insensitive query term entered by a user searching for something.
     * @param {Integer} [limit=null] - Limits the maximum number of suggestions in the response.
     * Required if you are also using the filter parameter.
     * @param {String} [filter=null] - Only return suggestions which also contain this string (case insensitive).
     * @desc Queries for search suggestions.
     * @returns {Promise<Object>} A list of search suggestions.
     *
     */
    getSearchSuggestions(suggestionSet, query, limit = null, filter = null) {
        const errorMessage = {
            method: 'ContentClient.getSearchSuggestions(suggestionSet, query, limit, filter)',
            get suggestionSet() {
                return `${this.method} needs suggestionSet instance`;
            },
            get query() {
                return `${this.method} needs query instance`;
            },
            get limit() {
                return `${this.method} limit should be a positive integer`;
            },
            get filter() {
                return `${this.method} filter should be a string with length`;
            }
        };

        nonEmptyString(suggestionSet, errorMessage.suggestionSet, InvalidArgumentException);
        nonEmptyString(query, errorMessage.query, InvalidArgumentException);

        if (Check.assigned(limit)) {
            integer(limit, errorMessage.limit, InvalidArgumentException);
            positive(limit, errorMessage.limit, InvalidArgumentException);
        }

        if (Check.assigned(filter)) {
            nonEmptyString(filter, errorMessage.filter, InvalidArgumentException);
        }

        const { dustEnabled, httpClient, logger } = this;
        const { endpoints } = this.config;
        const endpoint = endpoints[ContentClientEndpoint.searchSuggestions];
        const { href, headers } = endpoint;
        const requestHeaders = Object.assign({}, headers);

        let url = href;

        url = url.replace('{dictionary}', suggestionSet);
        url = url.replace('{query}', query);

        url = Check.integer(limit) && Check.positive(limit) ? `${url}?limit=${limit}` : `${url}?limit=`;
        url = Check.string(filter) && Check.nonEmptyString(filter) ? `${url}&filter=${filter}` : `${url}&filter=`;

        const payload = {
            url,
            headers: requestHeaders
        };

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: ContentClient.name,
            urn: ContentClientDustUrnReference.getSearchSuggestions,
            payload,
            method: HttpMethod.GET
        });

        return httpClient.get(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).then((response) => {

            const { data } = response;

            return Promise.resolve(data);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /* #region private: */

    /**
     *
     * @access private
     * @param {Object} options
     * @param {SDK.Services.Token.AccessToken} options.accessToken
     * @param {CatalogClientEndpoint} options.rel
     * @param {GraphQlBuilder} options.graphQlBuilder
     * @param {SDK.Content.SearchOverrides} [options.overrides]
     * @param {String} [options.contentTransactionId]
     * @returns {Object} The payload for the client call.
     *
     */
    getPayload(options) {

        const { accessToken, rel, graphQlBuilder, overrides, contentTransactionId } = options;
        const { endpoints, urlSizeLimit } = this.config;
        const { xDelorean, xGeoOverride, xContentTransactionId } = this;

        const endpoint = endpoints[rel];

        const { href, method, headers } = endpoint;

        const overrideHeaders = {};

        if (Check.assigned(overrides)) {

            const { activeDate, countryCode } = overrides;

            if (!!activeDate) {
                overrideHeaders[xDelorean] = activeDate;
            }

            if (!!countryCode) {
                overrideHeaders[xGeoOverride] = countryCode;
            }

        }

        if (Check.assigned(contentTransactionId)) {
            overrideHeaders[xContentTransactionId] = contentTransactionId;
        }


        const requestHeaders = Object.assign({}, headers, overrideHeaders);

        let requestMethod = method ? HttpMethod[method.toUpperCase()] : HttpMethod.POST;
        let url = graphQlBuilder.createQueryUrl(href, requestMethod);
        let requestBody = '';

        if (requestMethod === HttpMethod.GET && url.length > urlSizeLimit) {

            requestMethod = HttpMethod.POST;
            url = graphQlBuilder.createQueryUrl(href, requestMethod);

        }

        if (requestMethod === HttpMethod.GET) {
            delete requestHeaders['Content-Type'];
        } else if (requestMethod === HttpMethod.POST) {
            requestBody = graphQlBuilder.createPostContent();
        }

        requestHeaders.Authorization = requestHeaders.Authorization.replace(/\{accessToken\}/gi,
            accessToken.token);

        return {
            url,
            body: requestBody,
            method: requestMethod.toLowerCase(),
            headers: requestHeaders
        };

    }

}



// WEBPACK FOOTER //
// ./src/services/content/contentClient.js